import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import { analyticsAPI } from '../../api/itemEngagementAPI';
import { trackItemClicked } from '../../utils/analytics';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FeaturedStrip from '../../components/items/FeaturedStrip';
import CategoryCarousel from '../../components/items/CategoryCarousel';
import { DESIGNER_BROWSE_CATEGORIES } from '../../constants/designerBrowseCategories';
import { LUXURY_PIECE_CATEGORIES } from '../../constants/luxuryCategories';
import BrowseFiltersToolbar, { buildBrowseFetchParams } from '../../components/items/BrowseFiltersToolbar';
import { EMPTY_BROWSE_FILTERS, SHOP_DEPARTMENTS } from '../../constants/itemBrowseFilters';
import VerticalPageSeo from '../../components/seo/VerticalPageSeo';
import { getSectionBrand } from '../../constants/sectionBrands';
import { canOwnerManageShopInPortal } from '../../utils/shopOwnerPortal';
import { getDesignerItemPublicPath, getListingLinkState, getMarketplaceItemDetailPath } from '../../utils/itemLinks';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import { FAVORITE_KIND } from '../../utils/favoriteKeys';
import { luxuryBrowseAPI } from '../../api/luxuryBrowseAPI';
import { getApproximateViewerCoordinates } from '../../utils/viewerGeo';

const LuxuryPageShell = styled.div`
  min-height: calc(100vh - 120px);
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(196, 165, 116, 0.16), transparent 55%),
    linear-gradient(180deg, #ebe7e0 0%, #e2ddd4 48%, #dcd6cc 100%);
`;

const DesignersPageShell = styled.div`
  min-height: calc(100vh - 120px);
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(99, 102, 241, 0.16), transparent 55%),
    linear-gradient(180deg, #ebe9f4 0%, #e2dff0 48%, #d9d5e8 100%);
`;

const DesignersContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1rem 3rem;
`;

const PageDescription = styled.p`
  margin: 0 auto 0.5rem;
  text-align: center;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
  width: 100%;
  padding: 0 0.25rem;
  font-size: 0.8rem;
  @media (max-width: 768px) {
    font-size: 0.62rem;
  }
  @media (max-width: 400px) {
    font-size: 0.52rem;
  }
`;

// Items Grid
const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }
`;

const ItemCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  overflow: hidden;
  border: none;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.92;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-light);
  font-size: 3rem;
  position: relative;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const ItemContent = styled.div`
  padding: 0.55rem 0.15rem 0.25rem;
`;

const ItemPrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--text-light);
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;


const NoItemsMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
  }
`;

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`;

const IMAGE_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8080'
  : '';

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const CreateShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 4px 14px rgba(118, 75, 162, 0.35);
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(118, 75, 162, 0.45);
    color: white;
  }
`;

const CustomOrderCard = styled.section`
  background: linear-gradient(135deg, rgba(76, 29, 149, 0.95), rgba(91, 33, 182, 0.92));
  border-radius: 1rem;
  color: #fff;
  margin: 0 0 1.25rem;
  padding: 1.35rem 1.4rem;
  h2 { font-size: 1.45rem; margin: 0.35rem 0 0.45rem; }
  p { color: rgba(255, 255, 255, 0.88); line-height: 1.5; margin: 0 0 1rem; }
`;
const CustomOrderEyebrow = styled.div`
  color: rgba(255, 255, 255, 0.75); font-size: 0.75rem; font-weight: 800; letter-spacing: 0.08em;
`;
const CustomOrderActions = styled.div`display: flex; flex-wrap: wrap; gap: 0.65rem;`;
const CustomOrderLink = styled(Link)`
  align-items: center; background: ${(p) => (p.$secondary ? 'transparent' : '#fff')};
  border: 1.5px solid #fff; border-radius: 0.55rem;
  color: ${(p) => (p.$secondary ? '#fff' : '#5b21b6')}; display: inline-flex;
  font-weight: 800; min-height: 42px; padding: 0 0.95rem; text-decoration: none;
`;

const ManageShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: white;
  color: #764ba2;
  border: 2px solid #764ba2;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.2s;
  &:hover { background: #f3f0ff; }
`;

const LuxuryOfferButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: linear-gradient(135deg, #1c1917 0%, #292524 100%);
  color: #f5f2eb;
  border: 1px solid rgba(196, 165, 116, 0.55);
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(28, 25, 23, 0.22);
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    border-color: #c4a574;
    box-shadow: 0 10px 24px rgba(28, 25, 23, 0.28);
  }
`;

/* ─── Featured Designs ─────────────────────────────────────────── */
// eslint-disable-next-line no-unused-vars
const FeaturedSection = styled.div`
  margin-bottom: 2rem;
`;

// eslint-disable-next-line no-unused-vars
const FeaturedTrack = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem 0 1rem;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCard = styled(Link)`
  flex-shrink: 0;
  width: 160px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e5e7eb;
  text-decoration: none;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.12); }
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardImage = styled.div`
  width: 100%;
  height: 140px;
  background: #f3f4f6;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; object-position: center; }
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardBody = styled.div`
  padding: 0.6rem 0.75rem 0.75rem;
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardTitle = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardPrice = styled.p`
  font-size: 0.75rem;
  color: #764ba2;
  font-weight: 600;
  margin: 0;
`;

const LUXURY_BROWSE_CATEGORIES = [
  { name: 'All', icon: '💎', value: '' },
  ...LUXURY_PIECE_CATEGORIES.map((c) => ({ name: c.name, icon: c.icon, value: c.apiCategory })),
];

const DesignersPage = ({ brandKey = 'default' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brand = getSectionBrand(brandKey, 'default');
  const { isAuthenticated } = useSelector(s => s.auth);
  const [items, setItems] = useState([]);
  const [myShop, setMyShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({ ...EMPTY_BROWSE_FILTERS });
  const [sort, setSort] = useState('');
  const [viewerCoords, setViewerCoords] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const loadMoreRef = useRef(null);
  const loadMoreObserverRef = useRef(null);

  const designerImpSent = useRef(new Set());
  const designerObserverRef = useRef(null);

  useEffect(() => {
    const department = searchParams.get('department') || '';
    if (department && !SHOP_DEPARTMENTS.includes(department)) {
      return;
    }
    setBrowseFilters((prev) => (
      prev.department === department ? prev : { ...prev, department }
    ));
  }, [searchParams]);

  const designerCardRef = useCallback((node) => {
    if (!node) return;
    if (!designerObserverRef.current) {
      designerObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = Number(entry.target.dataset.designerItemId);
              if (id && !designerImpSent.current.has(id)) {
                designerImpSent.current.add(id);
                analyticsAPI.recordImpression(id, 'designer_item');
              }
            }
          });
        },
        { threshold: 0.5 },
      );
    }
    designerObserverRef.current.observe(node);
  }, []);

  useEffect(() => {
    return () => {
      analyticsAPI.flushImpressions();
      designerObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    getApproximateViewerCoordinates().then((c) => {
      if (c) setViewerCoords(c);
    });
  }, []);

  useEffect(() => {
    if (brandKey === 'luxury') {
      setMyShop(null);
      return;
    }
    if (isAuthenticated) {
      designerItemsAPI.getMyShop().then(shop => setMyShop(shop)).catch(() => {});
    }
  }, [isAuthenticated, brandKey]);

  const fetchPage = useCallback(async (targetPage, replace = false) => {
    const geoExtra = viewerCoords
      ? { user_lat: viewerCoords.lat, user_lon: viewerCoords.lon }
      : {};
    const filters = buildBrowseFetchParams(browseFilters, sort, geoExtra);
    if (replace) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const payload = brandKey === 'luxury'
        ? await luxuryBrowseAPI.getLuxuryListingsPage(filters, targetPage, 24, sort)
        : await designerItemsAPI.getDesignerItemsPage(filters, targetPage, 24);
      const nextResults = Array.isArray(payload?.results) ? payload.results : [];
      setItems(prev => (replace ? nextResults : [...prev, ...nextResults]));
      setPage(payload?.page || targetPage);
      setHasNext(Boolean(payload?.has_next));
      setTotalCount(payload?.count || nextResults.length);
    } catch (err) {
      console.error('DesignersPage: Failed to fetch items', err);
      if (replace) {
        setItems([]);
        setHasNext(false);
        setTotalCount(0);
      }
    } finally {
      if (replace) setLoading(false);
      else setLoadingMore(false);
    }
  }, [browseFilters, sort, brandKey, viewerCoords]);

  useEffect(() => {
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && hasNext && !loadingMore && !loading) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '300px 0px 300px 0px', threshold: 0.01 }
    );
    loadMoreObserverRef.current.observe(loadMoreRef.current);
    return () => loadMoreObserverRef.current?.disconnect();
  }, [hasNext, loadingMore, loading, page, fetchPage]);

  const handleCategorySelect = (apiCategory) => {
    setBrowseFilters((prev) => ({ ...prev, category: apiCategory, subcategory: '', book_subcategory: '', size: '' }));
  };

  const handleFiltersChange = (next) => {
    setBrowseFilters(next);
  };

  const getImageUrl = (item) => {
    if (item.primary_image) {
      return item.primary_image.startsWith('http') ? item.primary_image : `${IMAGE_BASE}${item.primary_image}`;
    }
    if (item.image_url) return item.image_url;
    if (item.images && item.images.length > 0) {
      const img = item.images[0].image;
      return img.startsWith('http') ? img : `${IMAGE_BASE}${img}`;
    }
    return null;
  };

  const browseCategories = brandKey === 'luxury' ? LUXURY_BROWSE_CATEGORIES : DESIGNER_BROWSE_CATEGORIES;

  if (loading && items.length === 0) {
    const spinner = <LoadingSpinner text="Loading items..." />;
    if (brandKey === 'luxury') return <LuxuryPageShell>{spinner}</LuxuryPageShell>;
    return <DesignersPageShell>{spinner}</DesignersPageShell>;
  }

  const pageContent = (
    <DesignersContainer>
      <VerticalPageSeo />

      <CategoryCarousel
        categories={browseCategories}
        selectedCategory={browseFilters.category || ''}
        onSelectCategory={handleCategorySelect}
        variant={brandKey === 'luxury' ? 'luxury' : 'purple'}
      />

      {brand.pageDescription ? (
        <PageDescription>{brand.pageDescription}</PageDescription>
      ) : null}
      {/* CTA buttons — shop management for designers; luxury listing for luxury hub */}
      {brandKey === 'luxury' ? (
        <HeroActions>
          <LuxuryOfferButton
            type="button"
            onClick={() => navigate(isAuthenticated ? '/list-luxury-piece' : '/login')}
          >
            ✨ Offer Pre-Owned Luxury Piece
          </LuxuryOfferButton>
        </HeroActions>
      ) : (
        <HeroActions>
          {canOwnerManageShopInPortal(myShop) ? (
            <ManageShopButton to={`/designer-shop/${designerItemsAPI.buildShopRef(myShop)}`}>
              {brand.myShop}
            </ManageShopButton>
          ) : (
            <CreateShopButton to="/create-shop">
              {brand.createShop}
            </CreateShopButton>
          )}
        </HeroActions>
      )}

      {brandKey !== 'luxury' ? (
        <CustomOrderCard>
          <CustomOrderEyebrow>MADE FOR YOU</CustomOrderEyebrow>
          <h2>Request a custom order from a designer</h2>
          <p>Choose a designer first, pick what they offer, describe your request, and ask for a quote.</p>
          <CustomOrderActions>
            <CustomOrderLink to="/custom-order/designers/start">Browse custom designers</CustomOrderLink>
            <CustomOrderLink to="/customization-requests" $secondary>My customization requests</CustomOrderLink>
            {canOwnerManageShopInPortal(myShop) ? (
              <CustomOrderLink to="/designer/customization-settings" $secondary>Manage custom orders</CustomOrderLink>
            ) : null}
          </CustomOrderActions>
        </CustomOrderCard>
      ) : null}

      {brandKey === 'luxury' && (
        <FeaturedStrip
          scope="luxury_items"
          department={browseFilters.department || ''}
        />
      )}
      <FeaturedStrip
        scope={brand.featuredScope}
        department={browseFilters.department || ''}
      />

      <BrowseFiltersToolbar
        filters={browseFilters}
        onFiltersChange={handleFiltersChange}
        categories={browseCategories}
        sort={sort}
        onSortChange={setSort}
        hideConditionFilter={brandKey !== 'luxury'}
      />

      {items.length === 0 ? (
        <NoItemsMessage>
          <h3>No items yet.</h3>
          <p>Try selecting a different category or be the first to list an item!</p>
        </NoItemsMessage>
      ) : (
        <>
          <ItemsGrid>
            {items.map((item) => {
              const imageUrl = getImageUrl(item);
              const hasMultipleImages = item.images && item.images.length > 1;
              const isMarketplaceLuxury = item.listingKind === 'item';
              const itemPath = isMarketplaceLuxury ? getMarketplaceItemDetailPath(item) : getDesignerItemPublicPath(item);
              const linkState = getListingLinkState(item, isMarketplaceLuxury ? 'item' : 'designer_item');
              const favoriteKind = isMarketplaceLuxury ? FAVORITE_KIND.ITEM : FAVORITE_KIND.DESIGNER;
              const itemType = isMarketplaceLuxury ? 'item' : 'designer_item';
              const impressionType = isMarketplaceLuxury ? 'item' : 'designer_item';
              return (
                <ItemCard
                  key={item.key || `${itemType}-${item.id}`}
                  to={itemPath}
                  state={linkState}
                  onClick={(e) => {
                    trackItemClicked(item.id, { item_type: itemType, category: item.category });
                    analyticsAPI.recordClick(item.id, impressionType);
                    e.preventDefault();
                    navigate(itemPath, { state: linkState });
                  }}
                  ref={isMarketplaceLuxury ? undefined : designerCardRef}
                  data-designer-item-id={isMarketplaceLuxury ? undefined : item.id}
                >
                  <ItemImage style={{ position: 'relative' }}>
                    <ItemFavoriteButton
                      itemId={item.id}
                      favoriteKind={favoriteKind}
                      category={item.category}
                    />
                    {imageUrl ? (
                      <>
                        <img
                          src={imageUrl}
                          alt={item.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.1)' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {hasMultipleImages && (
                          <div style={{
                            position: 'absolute', top: '8px', left: '8px',
                            backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                            padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600'
                          }}>
                            📷 {item.images.length}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', color: 'var(--text-light)' }}>
                        📦
                      </div>
                    )}
                  </ItemImage>
                  <ItemContent>
                    <ItemPrice>{formatItemPriceEgp(item.price)}</ItemPrice>
                    <ItemMeta>
                      <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
                      {isMarketplaceLuxury && item.condition ? <span>· {item.condition}</span> : null}
                    </ItemMeta>
                  </ItemContent>
                </ItemCard>
              );
            })}
          </ItemsGrid>
          {loadingMore && <LoadingSpinner text="Loading more designs..." />}
          {!hasNext && items.length > 0 && (
            <NoItemsMessage style={{ paddingTop: '1.2rem', paddingBottom: '1.2rem' }}>
              <p>Showing {items.length} of {totalCount} items.</p>
            </NoItemsMessage>
          )}
          <LoadMoreSentinel ref={loadMoreRef} />
        </>
      )}
    </DesignersContainer>
  );

  if (brandKey === 'luxury') {
    return <LuxuryPageShell>{pageContent}</LuxuryPageShell>;
  }

  return <DesignersPageShell>{pageContent}</DesignersPageShell>;
};

export default DesignersPage;
